import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Action, Actions, Selector, State, StateContext } from '@ngxs/store';
import { SortDescriptor } from '@progress/kendo-data-query';
import _, { cloneDeep } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { emailStringToContact } from 'src/app/helper/emailingHelpers';
import { samSort } from 'src/app/helper/sortingHelper';
import { LongRunningContextCategoryTyp } from 'src/app/samwebDescription/samweb/waitingContext/waitingContextCategory';
import { MessageEntryRemoteDto } from 'src/generated/signalR/message-entry-remote-dto';
import { SamwinMessageInfoRemoteDto } from 'src/generated/signalR/samwin-message-info-remote-dto';
import {
   EmailContentEntryRemoteDto,
   EmailContentResponse,
   EmailSearchResponse,
   EmailsService,
   MessageEntryRemoteDto as MessageEntries,
} from 'src/generated/swagger';
import { LongRunningContextStarted, LongRunningContextStopped } from '../../server-communication/running-actions/running-actions.actions';
import { Email } from '../email/email-behaviour/email';
import { EmailCreate } from '../email/email-behaviour/email-behaviour.actions';
import { MailingFolderEnum } from '../samwin-skills/mailing-folders-enum';
import { SamwinSkillsState } from '../samwin-skills/samwin-skills.state';
import { SkillConstants } from '../samwin-skills/skill-constants';
import { SkillGroup } from '../samwin-skills/skill-group';
import { MessageInfo } from './message-info';
import {
   CloseMessage,
   GetAttachment,
   GetAttachments,
   GetMessageContent,
   GetSearchedMessages,
   MarkMessageActive,
   MarkMessageUndone,
   MessageFilterChanged,
   MessageListChangeCustomSorting,
   MessageListChanged,
   MessageListEntrySelected,
   MessagesChanged,
   MessagesSearchStateChanged,
   MoveMessage,
   OpenMessage,
   ParkMessageOnAgent,
   ParkMessageOnSkill,
   ReQueueMessage,
   ReassignMessage,
   RemoveFromMessages,
   SearchValueChanged,
   SetMessageListSkillFilter,
   UpdateMessageDraggedStatus,
   UpdateMessageDroppableStatus,
   UpdateSearchValues,
} from './samwin-message-list.actions';

export interface SamwinMessageListStateModel {
   waitList: SamwinMessageInfoRemoteDto[];
   messages: { [messageId: string]: MessageEntryRemoteDto };
   currentContent: EmailContentEntryRemoteDto;
   filteredMessages: MessageEntries[];
   group: string;
   skill: number;
   searchValue: string;
   wasSearchExecuted: boolean;
   customSorting: SortDescriptor[];
   canMessageBeDropped: boolean;
   isMessageDragged: boolean;
   selectedRow: string;
   currentLoadingMessages: string[];
   waitListKeys: { [Callid: number]: string };
}

@State<SamwinMessageListStateModel>({
   name: 'samwinMessageList',
   defaults: {
      waitList: [],
      messages: {},
      currentContent: null,
      filteredMessages: [],
      group: MailingFolderEnum.INBOX.name,
      skill: null,
      searchValue: null,
      wasSearchExecuted: false,
      customSorting: [],
      canMessageBeDropped: false,
      isMessageDragged: false,
      selectedRow: '',
      currentLoadingMessages: [],
      waitListKeys: {},
   },
})
@Injectable()
export class SamwinMessageListState {
   constructor(private messageService: EmailsService, private actions$: Actions, protected httpClient: HttpClient, private sanitizer: DomSanitizer) {}

   public static sortMessages(messages: MessageInfo[], sortDescription: SortDescriptor[]) {
      if (!_.isEmpty(sortDescription)) {
         const sortedresult = samSort(messages, _.head(sortDescription)?.field ?? '', _.head(sortDescription)?.dir ?? 'desc');
         if (!_.isEmpty(sortedresult)) {
            return _(sortedresult).value();
         }
      }
      return messages;
   }

   @Selector([SamwinMessageListState])
   public static waitList(state: SamwinMessageListStateModel): SamwinMessageInfoRemoteDto[] {
      return state.waitList;
   }

   @Selector([SamwinMessageListState])
   public static messages(state: SamwinMessageListStateModel) {
      return state.messages;
   }

   @Selector([SamwinMessageListState])
   public static filteredMessages(state: SamwinMessageListStateModel) {
      return state.filteredMessages;
   }

   @Selector([SamwinMessageListState])
   public static customSorting(state: SamwinMessageListStateModel) {
      return state.customSorting;
   }

   @Selector([SamwinMessageListState])
   public static getWaitListKeys(state: SamwinMessageListStateModel): { [Callid: number]: string } {
      return state.waitListKeys;
   }

   @Selector([SamwinMessageListState])
   public static getCurrentContentIds(state: SamwinMessageListStateModel): string[] {
      return state?.currentLoadingMessages;
   }

   @Selector([SamwinMessageListState])
   public static getSearchValue(state: SamwinMessageListStateModel): string {
      return state.searchValue;
   }

   @Selector([SamwinMessageListState])
   public static getSkillFilter(state: SamwinMessageListStateModel): number {
      return state.skill;
   }

   @Selector([SamwinMessageListState])
   public static getGroupFilter(state: SamwinMessageListStateModel): string {
      return state.group;
   }

   @Selector([SamwinMessageListState])
   public static canMessageBeDropped(state: SamwinMessageListStateModel): boolean {
      return state.canMessageBeDropped;
   }

   @Selector([SamwinMessageListState])
   public static isMessageDragged(state: SamwinMessageListStateModel): boolean {
      return state.isMessageDragged;
   }

   @Selector([SamwinMessageListState])
   public static wasSearchEverExecuted(state: SamwinMessageListStateModel): boolean {
      return state.wasSearchExecuted;
   }

   @Selector([SamwinMessageListState.waitList])
   public static getWaitListItemById(waitList: SamwinMessageInfoRemoteDto[]) {
      return (messageId) => waitList.find((item) => item.messageId === messageId);
   }

   @Selector([SamwinMessageListState.waitList])
   public static getSkillProgressionFromWaitlist(waitList: SamwinMessageInfoRemoteDto[]) {
      return (messageId: string) => {
         return _(waitList)
            .filter((waitlistItem) => waitlistItem.messageId === messageId)
            .map((waitlistItem) => waitlistItem?.skillValue ?? SkillConstants.MAX_PROGRESSION)
            .head();
      };
   }

   @Selector([SamwinMessageListState.getGroupFilter])
   public static isInboxSelected(group: string): boolean {
      if (_.isNil(group) || _.isUndefined(group)) {
         return true;
      }
      return group === MailingFolderEnum.INBOX.name;
   }

   @Selector([SamwinMessageListState.getGroupFilter])
   public static isOutboxSelected(group: string): boolean {
      if (_.isNil(group) || _.isUndefined(group)) {
         return true;
      }
      return group === MailingFolderEnum.OUTBOX.name;
   }

   @Selector([SamwinMessageListState.getGroupFilter])
   public static getFilteredGroupTranslationValue(groupName: string): string {
      return MailingFolderEnum[groupName]?.translation ?? '';
   }

   @Selector([SamwinMessageListState.getGroupFilter])
   public static getSearchText(group: string): string {
      if (_.isNil(group)) {
         return 'common.search-text';
      }
      return 'brick.brick_definitions.mail_wait_list.searchtexts.' + group.toLowerCase();
   }

   @Selector([SamwinMessageListState])
   public static getFilteredSkillId(state: SamwinMessageListStateModel): number {
      return state.skill ?? SkillConstants.OVERALL_SKILL_ID;
   }

   @Selector([SamwinMessageListState.getFilteredSkillId, SamwinSkillsState.getSkillGroupById])
   public static getFilteredSkill(skillId: number, getSkillById: (id: number) => SkillGroup): SkillGroup {
      return getSkillById(skillId);
   }

   @Selector([SamwinMessageListState.getFilteredSkill])
   public static isFilteredBySkill(skill: SkillGroup): boolean {
      return !_.isNil(skill);
   }

   @Selector([SamwinMessageListState])
   public static getSortDescription(state: SamwinMessageListStateModel): SortDescriptor[] {
      return state.customSorting;
   }

   @Selector([SamwinMessageListState])
   public static selectedRowID(state: SamwinMessageListStateModel) {
      return state?.selectedRow;
   }

   @Selector([SamwinMessageListState.selectedRowID])
   public static selectedRowIDs(selectedRowID: string) {
      return [selectedRowID];
   }

   @Action(MessageListChangeCustomSorting)
   public messageListChangeCustomSorting(ctx: StateContext<SamwinMessageListStateModel>, action: MessageListChangeCustomSorting) {
      ctx.patchState({
         customSorting: action.customSorting,
      });
   }

   @Action(UpdateMessageDroppableStatus)
   public updateDroppableStatus(ctx: StateContext<SamwinMessageListStateModel>, action: UpdateMessageDroppableStatus) {
      ctx.patchState({
         canMessageBeDropped: action.canBeDropped,
      });
   }

   @Action(UpdateMessageDraggedStatus)
   public updateMessageDraggedStatus(ctx: StateContext<SamwinMessageListStateModel>, action: UpdateMessageDraggedStatus) {
      ctx.patchState({
         isMessageDragged: action.isDragged,
      });
   }

   @Action(MessageListChanged)
   public messageListChanged(ctx: StateContext<SamwinMessageListStateModel>, action: MessageListChanged) {
      const keys = {};
      _(action.updatedMailList).forEach((m) => (keys[m.callId] = m.messageId));

      ctx.patchState({
         waitList: action.updatedMailList,
         waitListKeys: keys,
      });
   }

   @Action(MessagesChanged)
   public messagesChanged(ctx: StateContext<SamwinMessageListStateModel>, action: MessagesChanged) {
      const state = ctx.getState();
      const updatedMessages = cloneDeep(state.messages);
      _(action.messages.messages.messages).forEach((m) => (updatedMessages[m.messageId] = m));
      _(action.messages.deletedMessageIds).forEach((id) => delete updatedMessages[id]);
      ctx.patchState({
         messages: updatedMessages,
      });
   }

   @Action(SearchValueChanged)
   public searchValueChanged(ctx: StateContext<SamwinMessageListStateModel>, action: SearchValueChanged) {
      ctx.patchState({
         searchValue: action.searchValue,
      });
   }

   @Action(MessageFilterChanged)
   public messageFilterChanged(ctx: StateContext<SamwinMessageListStateModel>, action: MessageFilterChanged) {
      ctx.patchState({
         group: action.groupName,
         skill: action.skillId,
      });
   }

   @Action(GetMessageContent)
   public getMessageContent(ctx: StateContext<SamwinMessageListStateModel>, action: GetMessageContent) {
      // NOTE: Set state to avoid multiple updates
      ctx.dispatch(new LongRunningContextStarted(LongRunningContextCategoryTyp.EMAIL_WINDOW, action.messageId));
      const state = ctx.getState();
      let messages = cloneDeep(state.currentLoadingMessages);
      if (!messages.includes(action.messageId)) {
         messages = messages.concat(action.messageId);
      }
      ctx.patchState({
         currentLoadingMessages: messages,
      });
      const messageContent: Observable<EmailContentEntryRemoteDto> = this.messageService.getEmailContent(action.messageId).pipe(
         map((response: EmailContentResponse) => response.content),
         catchError((error) => {
            console.error('Error during getEmailContent', error);
            return of(null);
         })
      );

      return messageContent
         .pipe(
            tap((content) => {
               if (_.isNil(content)) {
                  return;
               }
               ctx.patchState({
                  currentContent: content,
               });
            }),
            tap((content) => {
               if (_.isNil(content)) {
                  ctx.dispatch(new EmailCreate(action.messageId, null, []));
                  return;
               }
               ctx.dispatch(
                  new EmailCreate(
                     action.messageId,
                     new Email(
                        content?.sender,
                        emailStringToContact(content?.recipients),
                        emailStringToContact(content?.cc),
                        [],
                        content?.subject,
                        content?.content
                     ),
                     content?.messageAttachments
                  )
               );
            })
         )
         .pipe(
            tap(() => {
               const listState = ctx.getState();
               const loadedMessages = cloneDeep(listState.currentLoadingMessages);
               if (loadedMessages.includes(action.messageId)) {
                  _.remove(loadedMessages, (messageid) => messageid === action.messageId);
               }
               ctx.patchState({
                  currentLoadingMessages: loadedMessages,
               });
               ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.EMAIL_WINDOW, action.messageId));
            })
         );
   }

   @Action(MessagesSearchStateChanged)
   public messagesSearchStateChanged(ctx: StateContext<SamwinMessageListStateModel>, action: MessagesSearchStateChanged) {
      ctx.patchState({
         wasSearchExecuted: action.searchState,
      });
   }

   @Action(GetSearchedMessages)
   public getSearchedMessages(ctx: StateContext<SamwinMessageListStateModel>, action: GetSearchedMessages) {
      if (_.isNil(action.searchText) || _.isEmpty(_.trim(action.searchText)) || _.isNil(MailingFolderEnum[action.folder]?.value)) {
         return;
      }

      const filteredMessages: Observable<MessageEntries[]> = this.messageService
         .getFilteredEmails(action.searchText, MailingFolderEnum[action.folder].value, action.skill)
         .pipe(map((response: EmailSearchResponse) => response.messages?.messages ?? []));
      return filteredMessages.pipe(
         tap((entries) => {
            ctx.patchState({
               filteredMessages: entries,
            });
            if (action?.searchText?.length === 0) {
               ctx.dispatch(new MessagesSearchStateChanged(false));
            } else {
               ctx.dispatch(new MessagesSearchStateChanged(true));
            }
         })
      );
   }

   @Action(UpdateSearchValues)
   public updateSearchValues(ctx: StateContext<SamwinMessageListStateModel>, action: UpdateSearchValues) {
      const state = ctx.getState();
      return ctx.dispatch(new GetSearchedMessages(state.searchValue, state.group, state.skill));
   }

   @Action(OpenMessage)
   public openMessage(ctx: StateContext<SamwinMessageListStateModel>, action: OpenMessage) {
      return this.messageService.openEmail({
         callId: action.callId,
         messageId: action.messageId,
      });
   }

   @Action(CloseMessage)
   public closeMessage(ctx: StateContext<SamwinMessageListStateModel>, action: CloseMessage) {
      return this.messageService.closeEmail({
         callId: action.callId,
         messageId: action.messageId,
         folderId: action.folderId,
      });
   }

   @Action(ReassignMessage)
   public reassignMessage(ctx: StateContext<SamwinMessageListStateModel>, action: ReassignMessage) {
      return this.messageService.reassignEmail({
         callId: action.callId,
         messageId: action.messageId,
      });
   }

   @Action(MoveMessage)
   public moveMessage(ctx: StateContext<SamwinMessageListStateModel>, action: MoveMessage) {
      return this.messageService.moveEmail({
         messageId: action.messageId,
         newFolderId: action.newFolderId,
         newSkillId: action.newSkillId,
      });
   }

   @Action(RemoveFromMessages)
   public removeFromMessages(ctx: StateContext<SamwinMessageListStateModel>, action: RemoveFromMessages) {
      const state = ctx.getState();
      const updatedMessages = cloneDeep(state.messages);
      delete updatedMessages[action.messageId];

      const updatedFilteredMessages = _(state.filteredMessages)
         .filter((message) => message.messageId !== action.messageId)
         .value();

      const updatedWaitlist = _(state.waitList)
         .filter((message) => message.messageId !== action.messageId)
         .value();

      ctx.patchState({
         waitList: updatedWaitlist,
         messages: updatedMessages,
         filteredMessages: updatedFilteredMessages,
      });
   }

   @Action(MarkMessageActive)
   public markMessageActive(ctx: StateContext<SamwinMessageListStateModel>, action: MarkMessageActive) {
      return this.messageService.markEmailActive({
         callId: action.callId,
         hasFocus: action.hasFocus,
      });
   }

   @Action(MarkMessageUndone)
   public markMessageUndone(ctx: StateContext<SamwinMessageListStateModel>, action: MarkMessageUndone) {
      return this.messageService.markEmailUndone({
         callId: action.callId,
         messageId: action.messageId,
      });
   }

   @Action(ReQueueMessage)
   public reQueueMessage(ctx: StateContext<SamwinMessageListStateModel>, action: ReQueueMessage) {
      return this.messageService.reQueueEmail({
         callId: action.callId,
         queueId: action.queueId,
         messageId: action.messageId,
      });
   }

   @Action(ParkMessageOnSkill)
   public parkMessageOnSkill(ctx: StateContext<SamwinMessageListStateModel>, action: ParkMessageOnSkill) {
      return this.messageService.parkEmailOnSkill({
         callId: action.callId,
         queueId: action.queueId,
         messageId: action.messageId,
      });
   }

   @Action(ParkMessageOnAgent)
   public parkMessageOnAgent(ctx: StateContext<SamwinMessageListStateModel>, action: ParkMessageOnAgent) {
      return this.messageService.parkEmailOnAgent({
         callId: action.callId,
         messageId: action.messageId,
      });
   }

   @Action(GetAttachment)
   public getAttachment(ctx: StateContext<SamwinMessageListStateModel>, action: GetAttachment) {
      ctx.dispatch(new LongRunningContextStarted(LongRunningContextCategoryTyp.EMAIL_WINDOW, action.messageId));
      const basePath = '.';
      const path = `${basePath}/api/emails/attachment/${encodeURIComponent(action.attachment.uId)}`;
      const result = this.httpClient.get(path, { responseType: 'blob' });

      return result.subscribe((data) => {
         ctx.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.EMAIL_WINDOW, action.messageId));
         this.downloadBlobAsFile(data, action.attachment.name);
      });
   }

   @Action(GetAttachments)
   public getAttachments(ctx: StateContext<SamwinMessageListStateModel>, action: GetAttachments) {
      action.attachments.forEach((attachment) => {
         if (!_.isNil(attachment.uId)) {
            return ctx.dispatch(new GetAttachment(attachment, action.messageId));
         }
      });
   }

   private downloadBlobAsFile(blob: Blob, filename: string) {
      if (window.navigator.msSaveOrOpenBlob) {
         window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
         const element = window.document.createElement('a');
         element.href = window.URL.createObjectURL(blob);
         element.download = filename;
         document.body.appendChild(element);
         element.click();
         document.body.removeChild(element);
      }
   }

   @Action(MessageListEntrySelected)
   public messageListEntrySelected(ctx: StateContext<SamwinMessageListStateModel>, action: MessageListEntrySelected) {
      ctx.patchState({
         selectedRow: action.messageId,
      });
   }

   @Action(SetMessageListSkillFilter)
   public setMessageListSkillFilter(ctx: StateContext<SamwinMessageListStateModel>, action: SetMessageListSkillFilter) {
      return this.messageService.setMessageListSkillFilter({
         skillIds: action.skillIds ?? [],
      });
   }
}
